import {
  encryptionController,
  EncryptionKeys,
} from "controllers/EncryptionController"
import { getTokenInLocalStorage } from "helpers/utils"
import { cloneDeep, includes, isUndefined, pick } from "lodash"
import {
  InviteeBuilComponentDetail,
  ProjectBuildInvitessDetail,
  SendComponentInviteeRequest,
} from "pages/project-build/project-build.type"
import {
  getComponentSubBOMMiddleware,
  getPCBMiddleware,
  getPCBSubBOMMiddleware,
  getProjectComponentBOMMiddleware,
  getProjectComponentOtherMiddleware,
  syncNewVersionComponentInviteeMiddleWare,
} from "pages/project-component-detail/services"
import { PROJECT_COMPONENT_TYPE } from "pages/project-component/project-component.constant"
import forge from "node-forge"
import { encryptionHelper } from "helpers/encryption"
import { getProjectBuildInviteeMiddleware } from "pages/project-build/api.services"
import { MessageBackupType } from "components/FormBackup/type"
import { useState } from "react"
import { useBoolean } from "helpers/hooks"
import { activityLogHelper, ActivityLogType } from "helpers/activity_log"
import { createEncryptionKeysMiddleware } from "pages/projects/services/api"
import { EncryptionKeyInput, KeyEncryptionType } from "types"
import { generateDataKeyPairMiddleware } from "pages/auth/services/api"
import { encryptionGCMHelper } from "helpers/encryption-gcm"

export const useAddComponentInInvitee = () => {
  const [messageBackups, setMessageBackups] = useState<
    { content: string; type: MessageBackupType }[]
  >([])

  const [currentMessageBackup, setCurrentMessageBackup] =
    useState<MessageBackupType>(MessageBackupType.RETRIEVE_BACKUP_VERSION)
  const errorMessageBackup = useBoolean(false)
  const isLoadingBackupInvitee = useBoolean(false)

  const createNewComponentHistoryByOwner = async (
    projectId: string,
    componentType: number,
    projectComponentHistoryId: string,
    conversationId: string,
    currentShareKey?: string
  ) => {
    const dataComponentRequest: SendComponentInviteeRequest = {
      commit: "",
      componentType,
    }
    const userId = localStorage.getItem("user_id") || ""

    const projectKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
    )
    const projectKey = projectKeys[projectId]
    const sharedKey = currentShareKey || encryptionHelper.createRandomKey()
    const encryptSharedKey = (await encryptionGCMHelper.encryptContext(
      projectKey,
      sharedKey
    )) as string
    dataComponentRequest.encryptSharedKey = encryptSharedKey
    dataComponentRequest.sharedKey = sharedKey
    const privateKey = await encryptionGCMHelper.getUserPrivateKey(userId)
    if (!privateKey) {
      localStorage.clear()
      window.location.href = "/login"
      dataComponentRequest.errorMessage = (
        dataComponentRequest.errorMessage || []
      ).concat('"Private key not found"')
      return {
        dataComponentRequest,
        sharedKey,
      }
    }
    if (componentType === PROJECT_COMPONENT_TYPE.PCB) {
      await insertComponentInviteePCB(
        projectComponentHistoryId,
        dataComponentRequest,
        sharedKey,
        projectKey,
        projectId,
        conversationId
      )
      if (dataComponentRequest.boomFile) {
        let dataPCBSubBom = await getPCBSubBOMMiddleware(
          projectComponentHistoryId,
          undefined,
          undefined,
          projectId
        )
        if (
          !isUndefined(dataPCBSubBom.mouser_data) &&
          dataPCBSubBom.mouser_data.length
        ) {
          const encryptedMouser = (await encryptionGCMHelper.encryptContext(
            sharedKey,
            JSON.stringify(dataPCBSubBom.mouser_data)
          )) as string
          dataComponentRequest.mouser_data = encryptedMouser
        }
        if (
          !isUndefined(dataPCBSubBom.additional_json) &&
          dataPCBSubBom.additional_json.length
        ) {
          const newData: any = await Promise.all(
            dataPCBSubBom.additional_json.map(async (item) => {
              const encryptedValues = await encryptionGCMHelper.encryptContext(
                sharedKey,
                JSON.stringify(item.values)
              )
              return {
                ...item,
                values: encryptedValues,
              }
            })
          )
          dataComponentRequest.additionalJson = newData
        }
        dataPCBSubBom = null
      }
    }
    if (
      includes(
        [
          PROJECT_COMPONENT_TYPE.SOFTWARE,
          PROJECT_COMPONENT_TYPE.FIRMWARE,
          PROJECT_COMPONENT_TYPE.MECHANICAL,
          PROJECT_COMPONENT_TYPE.MISCELLANEOUS,
        ],
        componentType
      )
    ) {
      let type = "software"
      switch (componentType) {
        case PROJECT_COMPONENT_TYPE.MECHANICAL:
          type = "mechanical"
          break
        case PROJECT_COMPONENT_TYPE.FIRMWARE:
          type = "firmware"
          break
        case PROJECT_COMPONENT_TYPE.MISCELLANEOUS:
          type = "miscellaneous"
          break
      }
      const dataResOther = await insertComponentInviteeOther(
        projectComponentHistoryId,
        dataComponentRequest,
        type,
        sharedKey,
        projectKey,
        projectId,
        conversationId
      )
      if (
        componentType === PROJECT_COMPONENT_TYPE.MECHANICAL &&
        dataResOther.bom_id
      ) {
        let dataBoom = await getComponentSubBOMMiddleware(
          projectComponentHistoryId,
          type,
          undefined,
          undefined,
          projectId
        )

        if (dataBoom.file) {
          const bomFileMechanical = await handleReadAndUploadFile(
            dataBoom.file,
            projectComponentHistoryId,
            sharedKey,
            projectKey,
            conversationId
          )
          if (bomFileMechanical.success) {
            dataComponentRequest.boomFile = {
              file_name: bomFileMechanical.fileName,
            }
          } else {
            dataComponentRequest.errorMessage = (
              dataComponentRequest.errorMessage || []
            ).concat(bomFileMechanical.message)
          }
        }
        if (!isUndefined(dataBoom.mouser_data) && dataBoom.mouser_data.length) {
          const encryptedMouser = await encryptionGCMHelper.encryptContext(
            sharedKey,
            JSON.stringify(dataBoom.mouser_data)
          )
          dataComponentRequest.mouser_data = encryptedMouser
        }
        if (
          !isUndefined(dataBoom.additional_json) &&
          dataBoom.additional_json.length
        ) {
          const newData: any = await Promise.all(
            dataBoom.additional_json.map(async (item) => {
              const encryptedValues = await encryptionGCMHelper.encryptContext(
                sharedKey,
                JSON.stringify(item.values)
              )
              return {
                ...item,
                values: encryptedValues,
              }
            })
          )
          dataComponentRequest.additionalJson = newData
        }
        dataBoom = null
      }
    }

    if (componentType === PROJECT_COMPONENT_TYPE.BOM) {
      const dataBOM = await getProjectComponentBOMMiddleware(
        projectComponentHistoryId,
        projectId
      )

      if (dataBOM.file) {
        let bomFile = await handleReadAndUploadFile(
          dataBOM.file,
          projectComponentHistoryId,
          sharedKey,
          projectKey,
          conversationId
        )
        if (bomFile.success) {
          dataComponentRequest.boomFile = {
            file_name: bomFile.fileName,
          }
        } else {
          dataComponentRequest.errorMessage = (
            dataComponentRequest.errorMessage || []
          ).concat(bomFile.message as string)
        }
      }
      if (!isUndefined(dataBOM.mouser_data) && dataBOM.mouser_data.length) {
        let encryptedMouser = await encryptionGCMHelper.encryptContext(
          sharedKey,
          JSON.stringify(dataBOM.mouser_data)
        )
        dataComponentRequest.mouser_data = encryptedMouser
        encryptedMouser = null
      }
      if (
        !isUndefined(dataBOM.additional_json) &&
        dataBOM.additional_json.length
      ) {
        let newData: any = await Promise.all(
          dataBOM.additional_json.map(async (item) => {
            const encryptedValues = await encryptionGCMHelper.encryptContext(
              sharedKey,
              JSON.stringify(item.values)
            )
            return {
              ...item,
              values: encryptedValues,
            }
          })
        )
        dataComponentRequest.additionalJson = newData
        newData = null
      }
      if (dataBOM.projectComponentHistoryCommit) {
        let commit = await encryptionGCMHelper.decryptContext(
          projectKey,
          dataBOM.projectComponentHistoryCommit
        )
        let encryptCommit = await encryptionGCMHelper.encryptContext(
          sharedKey,
          commit
        )
        dataComponentRequest.commit = encryptCommit
        commit = null
        encryptCommit = null
      }
    }
    return {
      dataComponentRequest,
      sharedKey,
    }
  }

  const handleReadAndUploadFile = async (
    fileUrl: string,
    projectComponentHistoryId: string,
    sharedKey: string,
    projectKey: string,
    conversationId: string
  ) => {
    const userId = localStorage.getItem("user_id") || ""
    const result = await encryptionGCMHelper.readAndUploadFileShareComponent(
      fileUrl,
      projectKey,
      sharedKey,
      getTokenInLocalStorage(),
      projectComponentHistoryId,
      userId,
      conversationId
    )
    if (result.message === "Private key not found") {
      localStorage.clear()
      window.location.href = "/login"
    }
    return result
  }

  const insertComponentInviteePCB = async (
    projectComponentHistoryId: string,
    dataComponentRequest: SendComponentInviteeRequest,
    sharedKey: string,
    projectKey: string,
    projectId: string,
    conversationId: string
  ) => {
    const dataPCBRes = await getPCBMiddleware(
      projectComponentHistoryId,
      undefined,
      projectId
    )

    const fileFields = [
      { key: "assembly_file", target: "assemblyFile" },
      { key: "bom_file", target: "boomFile" },
      { key: "stackup_file", target: "stackupFile" },
      { key: "zip_file", target: "gerberFile" },
    ]

    for (const field of fileFields) {
      const fileUrl = dataPCBRes?.[field.key]
      if (fileUrl) {
        const fileData = await handleReadAndUploadFile(
          fileUrl,
          projectComponentHistoryId,
          sharedKey,
          projectKey,
          conversationId
        )
        if (fileData.success) {
          dataComponentRequest[field.target] = {
            file_name: fileData.fileName,
          }
        } else {
          dataComponentRequest.errorMessage = (
            dataComponentRequest.errorMessage || []
          ).concat(fileData.message)
        }
      }
    }

    if (dataPCBRes?.files?.length) {
      const attachments: any[] = []
      for (const attachment of dataPCBRes.files) {
        const result = await handleReadAndUploadFile(
          attachment.file,
          projectComponentHistoryId,
          sharedKey,
          projectKey,
          conversationId
        )
        if (result.success) {
          attachments.push({
            file_name: result.fileName,
          })
        } else {
          dataComponentRequest.errorMessage = (
            dataComponentRequest.errorMessage || []
          ).concat(result.message as string)
        }
      }
      dataComponentRequest.files = attachments
    }

    if (dataPCBRes?.tempLayers?.length) {
      const layers: any = []
      for (const layer of dataPCBRes.tempLayers) {
        const fileData = await handleReadAndUploadFile(
          layer.file,
          projectComponentHistoryId,
          sharedKey,
          projectKey,
          conversationId
        )

        if (!fileData.success) {
          dataComponentRequest.errorMessage = (
            dataComponentRequest.errorMessage || []
          ).concat(fileData.message as string)
        }
        const result = {
          ...layer,
          file_name_hash: fileData.fileName,
        }
        layers.push(result)
      }

      dataComponentRequest.pcbLayers = layers.map((layer) =>
        pick(layer, [
          "color",
          "custom_sort",
          "side",
          "type",
          "file_name",
          "sequence",
          "sort_index",
          "file_name_hash",
        ])
      ) as any
    }

    if (dataPCBRes.projectComponentHistoryCommit) {
      const commit = await encryptionGCMHelper.decryptContext(
        projectKey,
        dataPCBRes.projectComponentHistoryCommit
      )
      const encryptCommit = await encryptionGCMHelper.encryptContext(
        sharedKey,
        commit
      )
      dataComponentRequest.commit = encryptCommit
    }

    if (dataPCBRes.specifications?.length) {
      const specifications: { specification_part_id: string; value: string }[] =
        []
      let specificationsFiles: any[] = []
      for (const part of dataPCBRes.specifications[0].parts) {
        if (part.files?.length) {
          specificationsFiles = specificationsFiles.concat(
            part.files.map((el) => ({
              ...el,
              specification_part_id: part.id,
            }))
          )
        }
        specifications.push({
          specification_part_id: part.id,
          value: part.value,
        })
      }
      dataComponentRequest.specifications = specifications

      if (specificationsFiles.length) {
        let specificationHashFiles: any = []
        for (const elFile of specificationsFiles) {
          const fileData = await handleReadAndUploadFile(
            elFile.file,
            projectComponentHistoryId,
            sharedKey,
            projectKey,
            conversationId
          )
          const result = fileData.success
            ? {
                file_name: fileData.fileName,
                specification_part_id: elFile.specification_part_id,
              }
            : null
          if (!fileData.success) {
            dataComponentRequest.errorMessage = (
              dataComponentRequest.errorMessage || []
            ).concat(fileData.message as string)
          }
          specificationHashFiles.push(result)
        }
        dataComponentRequest.specificationFiles = specificationHashFiles.filter(
          Boolean
        ) as any
        specificationHashFiles = null
      }
    }

    if (dataPCBRes.specification_html_file) {
      const specificationHtmlFile = await handleReadAndUploadFile(
        dataPCBRes.specification_html_file,
        projectComponentHistoryId,
        sharedKey,
        projectKey,
        conversationId
      )
      if (specificationHtmlFile.success) {
        dataComponentRequest.specification_html_files =
          specificationHtmlFile.fileName
      } else {
        dataComponentRequest.errorMessage = (
          dataComponentRequest.errorMessage || []
        ).concat(specificationHtmlFile.message as string)
      }
    }
  }

  const insertComponentInviteeOther = async (
    projectComponentHistoryId: string,
    dataComponentRequest: SendComponentInviteeRequest,
    type: string,
    sharedKey: string,
    projectKey: string,
    projectId: string,
    conversationId: string
  ) => {
    const dataRes = await getProjectComponentOtherMiddleware(
      projectComponentHistoryId,
      type,
      undefined,
      undefined,
      projectId
    )
    if (dataRes?.files.length) {
      const attachments: any = []
      for (const attachment of dataRes?.files) {
        const result = await handleReadAndUploadFile(
          attachment.file,
          projectComponentHistoryId,
          sharedKey,
          projectKey,
          conversationId
        )
        if (result.success) {
          attachments.push({
            file_name: result.fileName,
          })
        } else {
          dataComponentRequest.errorMessage = (
            dataComponentRequest.errorMessage || []
          ).concat(result.message as string)
        }
      }
      dataComponentRequest.files = attachments
    }
    if (dataRes.projectComponentHistoryCommit) {
      let commit = await encryptionGCMHelper.decryptContext(
        projectKey,
        dataRes.projectComponentHistoryCommit
      )
      const encryptCommit = await encryptionGCMHelper.encryptContext(
        sharedKey,
        commit
      )
      dataComponentRequest.commit = encryptCommit
      commit = null
    }
    if (dataRes.description) {
      let encryptDescription = await encryptionGCMHelper.encryptContext(
        sharedKey,
        dataRes.description
      )
      dataComponentRequest.description = encryptDescription
      encryptDescription = null
    }
    if (dataRes.three_d_file) {
      let threeDFile = await handleReadAndUploadFile(
        dataRes.three_d_file,
        projectComponentHistoryId,
        sharedKey,
        projectKey,
        conversationId
      )

      if (threeDFile.success) {
        dataComponentRequest.threeDFile = {
          file_name: threeDFile.fileName,
        }
      } else {
        dataComponentRequest.errorMessage = (
          dataComponentRequest.errorMessage || []
        ).concat(threeDFile.message as string)
      }
    }
    return dataRes
  }

  const syncNewVersionComponentToInvitee = async (
    projectId: string,
    projectBuildId: string,
    projectComponentId: string,
    projectComponentHistoryId: string,
    conversationId: string,
    versionLasted: string,
    isLog: boolean = true,
    defaultInviteeComponents?: ProjectBuildInvitessDetail[]
  ) => {
    const conversationEncryptionKeys = JSON.parse(
      localStorage.getItem(EncryptionKeys.conversationEncryptionKeys) || "{}"
    )
    let inviteeComponents = defaultInviteeComponents
    if (isUndefined(defaultInviteeComponents)) {
      const listInvitee = await getProjectBuildInviteeMiddleware(projectBuildId)
      inviteeComponents = listInvitee.filter((el) =>
        includes(
          el.project_build_components.map((e) => e.project_component_id),
          projectComponentId
        )
      )
    }

    if (!inviteeComponents?.length) {
      return
    }

    const componentType = inviteeComponents[0].project_build_components.find(
      (el) => el.project_component_id === projectComponentId
    )?.type

    if (!componentType) {
      return
    }
    const messageBackups = [
      {
        content: `Retrieve data from components: ${versionLasted}`,
        type: MessageBackupType.RETRIEVE_BACKUP_VERSION,
      },
      {
        content: "Decrypting data context and files",
        type: MessageBackupType.DECRYPTING_BACKUP_VERSION,
      },
      {
        content: "Encrypting data context and files",
        type: MessageBackupType.ENCRYPTING_BACKUP_VERSION,
      },
      {
        content: "Creating share version",
        type: MessageBackupType.CREATING_BACKUP_FILE_VERSION,
      },
      {
        content: "Sync version to invitees",
        type: MessageBackupType.DONE_BACKUP_VERSION,
      },
    ]

    setMessageBackups(messageBackups)
    isLoadingBackupInvitee.setValue(true)
    setCurrentMessageBackup(MessageBackupType.RETRIEVE_BACKUP_VERSION)
    setTimeout(() => {
      setCurrentMessageBackup(MessageBackupType.DECRYPTING_BACKUP_VERSION)
    }, 200)

    const dataCreateNewVersion = await createNewComponentHistoryByOwner(
      projectId,
      componentType,
      projectComponentHistoryId,
      conversationId ? conversationId : inviteeComponents[0].conversation_id
    )

    setCurrentMessageBackup(MessageBackupType.ENCRYPTING_BACKUP_VERSION)
    const { dataComponentRequest, sharedKey } = dataCreateNewVersion

    if (dataComponentRequest.sharedKey) {
      console.log("-------START SYNC VERSION INVITEE-----------")
      console.log("Share key", dataComponentRequest.sharedKey)
      console.log("-------END SYNC VERSION INVITEE-----------")
      const inviteHashKeys: {
        encryptedKey: string
        project_component_history_id: string
        invitee_id: string
        user_id: string
      }[] = []

      const ccHashKeys: {
        encryptedKey: string
        user_id: string
      }[] = []

      const logs: {
        activity: string
        comment: string
        conversation_id: string
        component_code: string
      }[] = []

      setTimeout(() => {
        setCurrentMessageBackup(MessageBackupType.CREATING_BACKUP_FILE_VERSION)
      }, 200)
      for (const invite of inviteeComponents) {
        const history = invite.project_build_components.find(
          (el) => el.project_component_id === projectComponentId
        )
        if (history) {
          let encryptSharedKey = ""

          if (invite.publicKey) {
            const publicKey = forge.pki.publicKeyFromPem(invite.publicKey)
            encryptSharedKey = publicKey.encrypt(
              dataComponentRequest.sharedKey || "",
              "RSA-OAEP"
            )
          }

          inviteHashKeys.push({
            encryptedKey: invite.is_sent_invitee ? encryptSharedKey : "",
            project_component_history_id: history.project_component_history_id,
            invitee_id: invite.id,
            user_id: invite.user_id,
          })
        }
        if (invite.publicKeyCCs && invite.publicKeyCCs.length && history) {
          invite.publicKeyCCs.forEach((cc) => {
            if (cc.publicKey && cc.is_sent) {
              try {
                const publicKeyCC = forge.pki.publicKeyFromPem(
                  cc.publicKey as any
                )
                const encryptSharedKeyCC = publicKeyCC.encrypt(
                  sharedKey,
                  "RSA-OAEP"
                )
                ccHashKeys.push({
                  encryptedKey: encryptSharedKeyCC,
                  user_id: cc.user_id,
                })
              } catch (error) {}
            }
          })
        }
        if (invite.conversation_id && history && isLog) {
          const conversationEncryptionKey =
            conversationEncryptionKeys[invite.conversation_id]
          const component_code = `${history.code}/${versionLasted}`
          const comment = await activityLogHelper.toEncryptedMessage(
            ActivityLogType.CommentSyncDataFromBuild,
            {
              componentCode: component_code,
            },
            conversationEncryptionKey
          )
          const activity = await activityLogHelper.toEncryptedMessage(
            ActivityLogType.SyncFromBuild,
            {}
          )
          logs.push({
            activity,
            comment,
            conversation_id: invite.conversation_id,
            component_code,
          })
        }
      }
      let projectComponentHistoryShareId: string = ""
      const dataSyncRes = await syncNewVersionComponentInviteeMiddleWare(
        dataComponentRequest,
        inviteHashKeys,
        projectComponentHistoryId,
        projectBuildId,
        logs
      )
      if (dataSyncRes.project_component_history_share_id) {
        projectComponentHistoryShareId =
          dataSyncRes.project_component_history_share_id
      }
      if (ccHashKeys.length && projectComponentHistoryShareId) {
        await createEncryptionKeysMiddleware(
          ccHashKeys.map((el) => {
            return {
              encrypted_key: el.encryptedKey,
              model: "project_build_components",
              relation_id: projectComponentHistoryShareId,
              type: KeyEncryptionType.ComponentShareKey,
              user_id: el.user_id,
            }
          })
        )
      }
      setCurrentMessageBackup(MessageBackupType.DONE_BACKUP_VERSION)
      setTimeout(() => {
        isLoadingBackupInvitee.setValue(false)
      }, 2000)
    } else {
      errorMessageBackup.setValue(true)
      setTimeout(() => {
        isLoadingBackupInvitee.setValue(false)
      }, 2000)
    }
  }

  const getComponentShareKeys = (components: InviteeBuilComponentDetail[]) => {
    return components.filter(
      ({
        project_component_history_latest_id,
        project_component_history_id,
        project_component_history_share_id,
        componentShareKey,
      }) =>
        (project_component_history_latest_id &&
          project_component_history_id !==
            project_component_history_latest_id) ||
        !project_component_history_share_id ||
        !componentShareKey
    )
  }

  const handleMessageBackups = (
    newComponentShareKeys: InviteeBuilComponentDetail[]
  ) => {
    const messageBackups = [
      {
        content: `Retrieve data from component(s): ${newComponentShareKeys
          .map(
            (el) =>
              `<p style="display: flex;align-items: center;">${el.code}<svg style="margin-left:8px;margin-right:8px;" xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none"><circle cx="2" cy="2" r="2" fill="#7A7A7A"/></svg>${el.version}</p>`
          )
          .join("")}`,
        type: MessageBackupType.RETRIEVE_BACKUP_VERSION,
      },
      {
        content: "Decrypting data context and files",
        type: MessageBackupType.DECRYPTING_BACKUP_VERSION,
      },
      {
        content: "Encrypting data context and files",
        type: MessageBackupType.ENCRYPTING_BACKUP_VERSION,
      },
      {
        content: "Creating share version",
        type: MessageBackupType.CREATING_BACKUP_FILE_VERSION,
      },
      {
        content: "Sync version to invitees",
        type: MessageBackupType.DONE_BACKUP_VERSION,
      },
    ]
    setMessageBackups(messageBackups)
    isLoadingBackupInvitee.setValue(true)
  }

  const getLocalStorageItem = <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error)
      return defaultValue
    }
  }

  const getComponentHistoryId = (component: InviteeBuilComponentDetail) => {
    return component.project_component_history_latest_id &&
      component.project_component_history_latest_id !==
        component.project_component_history_id
      ? component.project_component_history_latest_id
      : component.project_component_history_id
  }

  const logComponentSharing = (
    originInvites: ProjectBuildInvitessDetail,
    sharedKey: string
  ) => {
    console.log("-------START SHARE COMPONENT-----------")
    console.log("Conversation code", originInvites.conversation_code)
    console.log("Conversation ID", originInvites.conversation_id)
    console.log("Share key", sharedKey)
    console.log(
      "Invitee",
      `${originInvites.name} / ${originInvites.email} / ${originInvites.user_id}`
    )
    console.log("CC", originInvites.cc)
    console.log("-------END SHARE COMPONENT-----------")
  }

  const generateActivityLogs = async (
    component: InviteeBuilComponentDetail,
    originInvites: ProjectBuildInvitessDetail,
    conversationEncryptionKeys: any
  ) => {
    const logs: {
      activity: string
      comment: string
      conversation_id: string
      component_code: string
      conversation_code: string
    }[] = []
    if (
      originInvites.conversation_id &&
      component.project_component_history_latest_id !==
        component.project_component_history_id
    ) {
      const conversationEncryptionKey =
        conversationEncryptionKeys[originInvites.conversation_id]
      logs.push({
        activity: await activityLogHelper.toEncryptedMessage(
          ActivityLogType.SyncFromBuild,
          {}
        ),
        comment: await activityLogHelper.toEncryptedMessage(
          ActivityLogType.CommentSyncDataFromBuild,
          {
            componentCode: `${component.code}/${component.version_code_latest}`,
          },
          conversationEncryptionKey
        ),
        conversation_id: originInvites.conversation_id,
        component_code: `${component.code}/${component.version_code_latest}`,
        conversation_code: originInvites.conversation_code,
      })
    }
    return logs
  }

  const updateComponentShareKeys = (
    components: InviteeBuilComponentDetail[],
    componentShares: any
  ) => {
    return components.map((el) => {
      const componentShareLatest = componentShares.find(
        (share) =>
          share.project_component_history_latest_id ===
            el.project_component_history_latest_id &&
          share.sharedKey &&
          share.project_component_history_share_id
      )
      return componentShareLatest
        ? {
            ...el,
            project_component_history_share_id:
              componentShareLatest.project_component_history_share_id || "",
            componentShareKey: componentShareLatest.sharedKey || "",
            project_component_history_id:
              componentShareLatest.project_component_history_id || "",
            project_component_history_latest_id:
              componentShareLatest.project_component_history_latest_id,
          }
        : el
    })
  }

  const generateEncryptionKeys = async (
    components: InviteeBuilComponentDetail[],
    originInvites: ProjectBuildInvitessDetail,
    projectId: string,
    generateDataKeyPairUsers: any[],
    emails?: string[]
  ) => {
    const keys: EncryptionKeyInput[] = []
    const projectKeys = getLocalStorageItem(
      EncryptionKeys.projectEncryptionKeys,
      {}
    )
    const buildAdditionalInfoEncryptionKeys = getLocalStorageItem(
      EncryptionKeys.buildAdditionalInfoEncryptionKeys,
      {}
    )
    const conversationEncryptionKeys = getLocalStorageItem(
      EncryptionKeys.conversationEncryptionKeys,
      {}
    )

    const projectKey = projectKeys[projectId]
    const conversationKeyItem =
      conversationEncryptionKeys[originInvites.conversation_id]
    const buildAdEncryptionKeyItem =
      buildAdditionalInfoEncryptionKeys[originInvites.project_build_id]

    if (projectKey && buildAdEncryptionKeyItem && conversationKeyItem) {
      const isAddKeyInvitee =
        (!isUndefined(emails) && includes(emails, originInvites.email)) ||
        isUndefined(emails)
      let publicKeyInvitee = originInvites.publicKey
      let publicKeyPemInvitee: forge.pki.rsa.PublicKey | null = null
      if (isAddKeyInvitee) {
        if (!publicKeyInvitee) {
          const dataKeyPair = await generateDataKeyPairMiddleware()
          publicKeyInvitee = dataKeyPair.publicKey
          generateDataKeyPairUsers.push({
            publicKey: dataKeyPair.publicKey,
            encryptedPrivateKey: dataKeyPair.encryptedPrivateKey,
            userId: originInvites.user_id,
          })
        }
        publicKeyPemInvitee = forge.pki.publicKeyFromPem(publicKeyInvitee)

        try {
          const buildInviteeEncryptionKey = publicKeyPemInvitee.encrypt(
            buildAdEncryptionKeyItem as string,
            "RSA-OAEP"
          )
          keys.push({
            encrypted_key: buildInviteeEncryptionKey,
            model: "build_additional_infos",
            relation_id: originInvites.project_build_id,
            type: KeyEncryptionType.ModuleKey,
            user_id: originInvites.user_id,
          })

          const conversationInviteeEncryptionKey = publicKeyPemInvitee.encrypt(
            conversationKeyItem as string,
            "RSA-OAEP"
          )
          keys.push({
            encrypted_key: conversationInviteeEncryptionKey,
            model: "conversations",
            relation_id: originInvites.conversation_id,
            type: KeyEncryptionType.ModuleKey,
            user_id: originInvites.user_id,
          })
        } catch (error) {
          console.log(error, originInvites.email)
        }
      }
      const listCCAdd: {
        publicKey: string
        publicKeyPem: forge.pki.rsa.PublicKey | null
        userId: string
      }[] = []

      // Add key conversation and build in list cc
      if (originInvites.publicKeyCCs && originInvites.publicKeyCCs.length) {
        for (const cc of originInvites.publicKeyCCs) {
          let publicKeyCC = cc.publicKey
          let publicKeyPemCC: forge.pki.rsa.PublicKey | null = null
          const isAddKeyCC =
            (!isUndefined(emails) && includes(emails, cc.email)) ||
            isUndefined(emails)
          if (isAddKeyCC) {
            if (!publicKeyCC) {
              const dataKeyPairCC = await generateDataKeyPairMiddleware()
              publicKeyCC = dataKeyPairCC.publicKey
              generateDataKeyPairUsers.push({
                publicKey: dataKeyPairCC.publicKey,
                encryptedPrivateKey: dataKeyPairCC.encryptedPrivateKey,
                userId: cc.user_id,
              })
            }
            publicKeyPemCC = forge.pki.publicKeyFromPem(publicKeyCC)
            listCCAdd.push({
              publicKey: publicKeyCC,
              publicKeyPem: publicKeyPemCC,
              userId: cc.user_id,
            })
            try {
              const buildCCEncryptionKey = publicKeyPemCC.encrypt(
                buildAdEncryptionKeyItem as string,
                "RSA-OAEP"
              )
              keys.push({
                encrypted_key: buildCCEncryptionKey,
                model: "build_additional_infos",
                relation_id: originInvites.project_build_id,
                type: KeyEncryptionType.ModuleKey,
                user_id: cc.user_id,
              })

              const conversationCCEncryptionKey = publicKeyPemCC.encrypt(
                conversationKeyItem as string,
                "RSA-OAEP"
              )
              keys.push({
                encrypted_key: conversationCCEncryptionKey,
                model: "conversations",
                relation_id: originInvites.conversation_id,
                type: KeyEncryptionType.ModuleKey,
                user_id: cc.user_id,
              })
            } catch (error) {
              console.log(error, cc.email)
            }
          }
        }
      }

      for (const elBuildComponentKey of components) {
        const sharedKey = await encryptionController().decrypt(
          elBuildComponentKey.componentShareKey,
          {
            dataType: "string",
            type: "component-shared",
            encryptionKey: projectKey,
          }
        )
        if (elBuildComponentKey.project_component_history_share_id) {
          // add key invitee
          if (publicKeyInvitee && sharedKey && publicKeyPemInvitee) {
            const encryptSharedKeyInvitee = publicKeyPemInvitee.encrypt(
              sharedKey,
              "RSA-OAEP"
            )
            if (isAddKeyInvitee) {
              keys.push({
                encrypted_key: encryptSharedKeyInvitee,
                model: "project_build_components",
                relation_id:
                  elBuildComponentKey.project_component_history_share_id,
                type: KeyEncryptionType.ComponentShareKey,
                user_id: originInvites.user_id,
              })
            }
          }
          // add key project_build_components  in list cc
          if (listCCAdd.length) {
            for (const addCC of listCCAdd) {
              if (
                addCC.publicKey &&
                sharedKey &&
                addCC.userId &&
                addCC.publicKeyPem
              ) {
                try {
                  const encryptSharedKeyCC = addCC.publicKeyPem.encrypt(
                    sharedKey,
                    "RSA-OAEP"
                  )
                  keys.push({
                    encrypted_key: encryptSharedKeyCC,
                    model: "project_build_components",
                    relation_id:
                      elBuildComponentKey.project_component_history_share_id ||
                      "",
                    type: KeyEncryptionType.ComponentShareKey,
                    user_id: addCC.userId,
                  })
                } catch (error) {}
              }
            }
          }
        }
      }
    }
    return keys
  }

  const finalizeMessageBackup = () => {
    setCurrentMessageBackup(MessageBackupType.DONE_BACKUP_VERSION)
    setTimeout(() => {
      isLoadingBackupInvitee.setValue(false)
    }, 2000)
  }

  const syncNewVersionComponentInvitee = async (
    inviteBuilComponent: InviteeBuilComponentDetail[],
    projectId: string,
    originInvites: ProjectBuildInvitessDetail,
    projectBuildId: string,
    conversationId: string,
    emails?: string[]
  ) => {
    let newInviteBuilComponent = cloneDeep(inviteBuilComponent)

    const generateDataKeyPairUsers: {
      publicKey: string
      encryptedPrivateKey: string
      userId: string
    }[] = []

    // Step 1: Identify component share keys
    const componentShareKeys = getComponentShareKeys(newInviteBuilComponent)
    console.log("Email select", emails)

    if (componentShareKeys.length) {
      // Step 2: Handle message backups
      handleMessageBackups(componentShareKeys)

      const conversationEncryptionKeys = getLocalStorageItem(
        EncryptionKeys.conversationEncryptionKeys,
        {}
      )
      setCurrentMessageBackup(MessageBackupType.RETRIEVE_BACKUP_VERSION)

      const componentShares: any = []

      for (const elComponentShareKey of componentShareKeys) {
        const { dataComponentRequest, sharedKey } =
          await createNewComponentHistoryByOwner(
            projectId,
            elComponentShareKey.type,
            getComponentHistoryId(elComponentShareKey),
            conversationId
          )

        if (dataComponentRequest.sharedKey) {
          if (!dataComponentRequest.errorMessage?.length) {
            logComponentSharing(originInvites, sharedKey)

            const logs = await generateActivityLogs(
              elComponentShareKey,
              originInvites,
              conversationEncryptionKeys
            )
            const dataRes = await syncNewVersionComponentInviteeMiddleWare(
              dataComponentRequest,
              [
                {
                  encryptedKey: "",
                  project_component_history_id:
                    elComponentShareKey.project_component_history_id,
                  invitee_id: originInvites.id,
                  user_id: originInvites.user_id,
                },
              ],
              getComponentHistoryId(elComponentShareKey),
              projectBuildId,
              logs
            )

            componentShares.push({
              project_component_history_share_id:
                dataRes?.project_component_history_share_id || "",
              project_component_history_id:
                getComponentHistoryId(elComponentShareKey),
              project_component_history_latest_id:
                getComponentHistoryId(elComponentShareKey),
              sharedKey: dataComponentRequest.encryptSharedKey || "",
            })
          }
        }
      }
      setTimeout(() => {
        setCurrentMessageBackup(MessageBackupType.DECRYPTING_BACKUP_VERSION)
      }, 200)
      setTimeout(() => {
        setCurrentMessageBackup(MessageBackupType.ENCRYPTING_BACKUP_VERSION)
      }, 400)
      setCurrentMessageBackup(MessageBackupType.CREATING_BACKUP_FILE_VERSION)

      // Step 3: Update newInviteBuilComponent with shared keys
      newInviteBuilComponent = updateComponentShareKeys(
        newInviteBuilComponent,
        componentShares
      )
    }
    // Step 4: Encrypt and distribute keys
    const keys = await generateEncryptionKeys(
      newInviteBuilComponent,
      originInvites,
      projectId,
      generateDataKeyPairUsers,
      emails
    )

    if (keys.length) {
      await createEncryptionKeysMiddleware(keys)
    }

    if (!isUndefined(emails)) {
      finalizeMessageBackup()
    }

    return generateDataKeyPairUsers
  }

  return {
    createNewComponentHistoryByOwner,
    syncNewVersionComponentToInvitee,
    syncNewVersionComponentInvitee,
    messageBackups,
    currentMessageBackup,
    errorMessageBackup,
    isLoadingBackupInvitee,
    setCurrentMessageBackup,
    setMessageBackups,
  }
}
